/**
 * Data Manager Service for handling regions and evaluation criteria API calls
 */

import { defaultErrorHandler as commonErrorHandler, createHeaders as createCommonHeaders, getApiUrls } from './serviceConfig';

/**
 * Creates a configured data manager service
 * @param {Object} config - Configuration options
 * @param {Object} config.headers - Additional headers to include in all requests
 * @param {Function} config.onError - Global error handler
 * @param {Function} config.getAuthToken - Function to get the latest auth token (preferred)
 * @param {string} config.authToken - Static authentication token for API requests (not recommended)
 * @returns {Object} The data manager service API
 */
export function createDataManagerService(config = {}) {
  const DEFAULT_HEADERS = config.headers || {};
  const defaultErrorHandler = config.onError || ((error) => commonErrorHandler('Data Manager service', error));
  const getAuthToken = config.getAuthToken || (() => config.authToken);

  /**
   * Helper to create request headers
   * @param {Object} additionalHeaders - Additional headers to include
   * @returns {Object} Headers object
   */
  const createHeaders = (additionalHeaders = {}) => {
    const customAuthGetter = config.getAuthToken ? getAuthToken : null;
    return createCommonHeaders(
      {
        ...DEFAULT_HEADERS,
        ...additionalHeaders,
      },
      customAuthGetter
    );
  };

  /**
   * Helper to handle API errors
   * @param {Response} response - Fetch Response object
   * @param {string} errorPrefix - Prefix for error message
   * @returns {Promise} JSON response or throws error
   */
  const handleResponse = async (response, errorPrefix = 'API error') => {
    if (!response.ok) {
      let errorMessage = `${errorPrefix}: ${response.status}`;

      try {
        const errorData = await response.json();
        if (errorData?.message) {
          errorMessage = `${errorPrefix}: ${errorData.message}`;
        }
      } catch {
        // If we can't parse the error JSON, just use the status code
      }

      const error = new Error(errorMessage);
      error.statusCode = response.status;
      throw error;
    }

    return response.json();
  };

  return {
    /**
     * Get regions list for a country
     * @param {string} countryCode - Country ISO code (e.g. 'ES')
     * @returns {Promise<Array<{code: string, name: string, countryCode: string}>>} List of regions
     */
    getRegions: async (countryCode) => {
      try {
        const urls = getApiUrls();
        const response = await fetch(urls.dataManager.regions(countryCode), {
          method: 'GET',
          headers: createHeaders(),
        });

        const data = await handleResponse(response, 'Error fetching regions');
        return Array.isArray(data) ? data : data?.data ?? [];
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },

    /**
     * Get evaluation criteria for a discipline and region
     * @param {Object} options - Query options
     * @param {string} options.disciplineId - Discipline id
     * @param {string} options.regionCode - Region code (e.g. 'ES-AN')
     * @param {string} [options.educationYear] - Optional education year (implicitly carries education level)
     * @returns {Promise<Array<Object>>} List of evaluation criteria
     */
    getEvaluationCriteria: async ({ disciplineId, regionCode, educationYear } = {}) => {
      try {
        const params = {};
        if (disciplineId) params.disciplineId = disciplineId;
        if (regionCode) params.regionCode = regionCode;
        if (educationYear) params.educationYear = educationYear;

        const urls = getApiUrls();
        const response = await fetch(urls.dataManager.evaluationCriteria(params), {
          method: 'GET',
          headers: createHeaders(),
        });

        const data = await handleResponse(response, 'Error fetching evaluation criteria');
        return Array.isArray(data) ? data : data?.data ?? [];
      } catch (error) {
        defaultErrorHandler(error);
        throw error;
      }
    },
  };
}

// Default export with default configuration
export default createDataManagerService();
